import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import { getAppConfig } from "./AppConfig";
import { getAccessToken } from "./auth/tokenFunctions";
import { tokenRefreshManager } from "./auth/tokenRefreshManager";
import { captureError } from "./telemetry";
import { createCorrelationId } from "./telemetry/createCorrelationId";
import { sanitizeErrorForLog } from "./auth/sanitizeErrorForLog";

declare module "axios" {
  export interface AxiosRequestConfig {
    _retry?: boolean;
    _correlationId?: string;
  }
}

function stripQueryString(url?: string): string {
  if (!url) {
    return "";
  }
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

const MAX_RESPONSE_DATA_LENGTH = 500;

// Backend error responses are ProblemDetails (title/detail/status), not patient data, but
// this is a medical app - truncate defensively in case a response ever echoes request input.
function summarizeResponseData(data: unknown): string | null {
  if (data === undefined || data === null) {
    return null;
  }
  try {
    const serialized = typeof data === "string" ? data : JSON.stringify(data);
    return serialized.length > MAX_RESPONSE_DATA_LENGTH
      ? `${serialized.slice(0, MAX_RESPONSE_DATA_LENGTH)}…`
      : serialized;
  } catch {
    return null;
  }
}

function captureAxiosError(
  error: AxiosError,
  kind: "network" | "server" | "token-refresh"
): void {
  const config = error.config as AxiosRequestConfig | undefined;
  captureError(error, {
    source: "axios",
    kind,
    status: error.response?.status ?? null,
    method: config?.method ?? null,
    url: stripQueryString(config?.url),
    correlationId: config?._correlationId ?? null,
    responseData: summarizeResponseData(error.response?.data),
  });
}

export function getAxiosApi() {
  const restBaseUrl = getAppConfig()?.restBaseUrl;
  const apiKey = getAppConfig()?.apiKey;

  const instance = axios.create({
    baseURL: restBaseUrl ?? "INVALID_CONFIG_URL/",
    timeout: 30000,
    headers: { "X-API-KEY": apiKey ?? "MISSING_API_KEY" },
  });
  return instance;
}

export function getAxiosInstance() {
  const instance = getAxiosApi();

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        const correlationId = createCorrelationId();
        config._correlationId = correlationId;
        config.headers.set("X-Correlation-Id", correlationId);

        const token = await getAccessToken();
        if (token) {
          config.headers.set("Authorization", `Bearer ${token}`);
        } else {
          // No token available - this will result in 401, but let it proceed
          console.warn("[axios-instance] No access token available for request");
        }
        return config;
      } catch (error) {
        // If getting access token fails, reject the request
        console.error("[axios-instance] Failed to get access token for request", sanitizeErrorForLog(error));
        return Promise.reject(error);
      }
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig;

      // Only handle 401 errors and don't retry if already retried
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          console.log("[axios-instance] Got 401, attempting token refresh");
          const newToken = await tokenRefreshManager.refreshAccessToken("401 error from API");

          // Retry the original request with the new token
          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;

          console.log("[axios-instance] Retrying original request with new token");
          // Note: this goes through raw `axios`, not `instance` - it will not
          // re-enter this response interceptor, so if the retry itself fails it
          // simply rejects here uncaptured (this catch only wraps the refresh
          // call above). That keeps this branch to exactly one capture: the
          // refresh failure itself, never a duplicate for the retried request.
          return axios(originalRequest);
        } catch (refreshError) {
          console.error("[axios-instance] Token refresh failed, user will be redirected", sanitizeErrorForLog(refreshError));
          // The tokenRefreshManager already handles logout and redirect.
          // This is a real failure (it logs the user out) - always capture it exactly once.
          if (axios.isAxiosError(refreshError)) {
            captureAxiosError(refreshError, "token-refresh");
          } else {
            captureError(refreshError, {
              source: "axios",
              kind: "token-refresh",
              status: null,
              method: originalRequest.method ?? null,
              url: stripQueryString(originalRequest.url),
              correlationId: originalRequest._correlationId ?? null,
            });
          }
          return Promise.reject(refreshError);
        }
      }

      // Not a 401 (or already retried and failed again) - capture network/server
      // failures here. 401 itself is expected/handled above and never captured.
      //
      // This is the richest layer for an HTTP failure (it has status, method, path and the
      // correlation id linking to the backend event), so it captures first. The same error
      // object then reaches React Query's onError and possibly window.unhandledrejection;
      // captureError dedupes by error identity so those later layers are no-ops.
      if (!error.response) {
        captureAxiosError(error, "network");
      } else if (error.response.status >= 500) {
        captureAxiosError(error, "server");
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

export function getAxiosPublicInstance() {
  const restBaseUrl = getAppConfig()?.restBaseUrl;
  const apiKey = getAppConfig()?.apiKey;

  const instance = axios.create({
    baseURL: restBaseUrl ?? "INVALID_CONFIG_URL/",
    timeout: 1000,
    headers: { "X-API-KEY": apiKey ?? "MISSING_API_KEY" },
  });

  // Correlation id only - this instance is used for unauthenticated/health calls
  // with a short 1s timeout, so network errors are routine and are intentionally
  // NOT captured here (that would flood the project with expected timeouts).
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.headers.set("X-Correlation-Id", createCorrelationId());
    return config;
  });

  return instance;
}
